<template>
  <div class="metro-distance-chart">
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>正在计算站点间距离...</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>
    
    <div v-else class="distance-content">
      <!-- 紧凑型距离排行表格 -->
      <div class="compact-distance-table">
        <div class="table-header">
          <div class="header-rank">排名</div>
          <div class="header-stations">站点区间</div>
          <div class="header-distance">距离</div>
          <div class="header-line">线路</div>
        </div>
        
        <div 
          v-for="(item, index) in topDistances" 
          :key="`${item.from}-${item.to}-${item.line}`"
          class="distance-row"
          :class="{ 
            'top-rank': index < 3,
            'highlighting': isHighlighting
          }"
          :style="{ animationDelay: `${index * 0.05}s` }"
          :title="isHighlighting ? '点击可切换到新的高亮线路段' : `点击可高亮 ${item.line} ${item.from}至${item.to} 线路段`"
          @click="handleSegmentClick(item)"
        >
          <!-- 排名 -->
          <div class="rank-cell">
            <div class="rank-number" :class="`rank-${index + 1}`">
              {{ index + 1 }}
            </div>
          </div>
          
          <!-- 站点区间 -->
          <div class="stations-cell">
            <div class="station-connection">
              <span 
                class="station-name clickable" 
                :title="`点击跳转至 ${item.from} 站`"
                @click.stop="handleStationClick(item.from)"
              >{{ item.from }}</span>
              <div class="connection-arrow">
                <svg width="20" height="12" viewBox="0 0 20 12">
                  <line x1="2" y1="6" x2="16" y2="6" stroke="#666" stroke-width="2"/>
                  <polygon points="14,3 18,6 14,9" fill="#666"/>
                </svg>
              </div>
              <span 
                class="station-name clickable" 
                :title="`点击跳转至 ${item.to} 站`"
                @click.stop="handleStationClick(item.to)"
              >{{ item.to }}</span>
            </div>
          </div>
          
          <!-- 距离 -->
          <div class="distance-cell">
            <div class="distance-info">
              <span class="distance-value">{{ item.distance }}</span>
              <span class="distance-unit">km</span>
            </div>
            <div class="distance-bar-mini">
              <div 
                class="distance-fill"
                :style="{ 
                  width: getDistancePercentage(item.distanceRaw) + '%',
                  backgroundColor: item.lineColor
                }"
              ></div>
            </div>
          </div>
          
          <!-- 线路编号 -->
          <div class="line-cell">
            <div 
              class="line-badge-compact"
              :style="{ 
                backgroundColor: item.lineColor,
                color: getTextColor(item.lineColor)
              }"
              @click.stop="handleLineClick(item.line)"
            >
              {{ getLineNumber(item.line) }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- 图表说明 -->
      <div class="chart-note">
        <p>注：仅统计运营中的主要线路（1-18号线），距离计算使用Haversine公式</p>
      </div>
      
      <!-- 数据来源注释 -->
      <div class="data-source">
        数据来源：上海地铁官方网站
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, inject } from 'vue'

interface StationData {
  sname: string
  rname: string
  order: number
  lat: number
  lng: number
  fork: number
}

interface DistanceItem {
  from: string
  to: string
  line: string
  distance: string
  distanceRaw: number
  lineColor: string
  fromCoords: { lat: number; lng: number }
  toCoords: { lat: number; lng: number }
}

const loading = ref(true)
const error = ref('')
const stations = ref<StationData[]>([])
const lineColors = ref<any>(null)
const topDistances = ref<DistanceItem[]>([])

// 注入跳转方法
const jumpToStation = inject<(stationName: string) => void>('jumpToStation')
const highlightLineSegment = inject<(fromStation: string, toStation: string, lineName: string) => void>('highlightLineSegment')

// 防抖状态
const isHighlighting = ref(false)
const highlightResetTimer = ref<number | null>(null)

// 计算两点间距离（Haversine公式）
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371 // 地球半径（公里）
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

// 获取线路颜色
const getLineColor = (lineName: string): string => {
  if (!lineColors.value?.data?.lines) return '#999999'
  
  // 提取线路号码
  const lineNumber = lineName.replace(/[^\d]/g, '')
  if (!lineNumber) return '#999999'
  
  const line = lineColors.value.data.lines.find((l: any) => 
    l && l.number && l.number.toString() === lineNumber
  )
  
  return line?.color || '#999999'
}

// 根据背景色计算文字颜色
const getTextColor = (backgroundColor: string): string => {
  // 简单的亮度判断
  const hex = backgroundColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 128 ? '#000000' : '#ffffff'
}

// 提取线路编号
const getLineNumber = (lineName: string): string => {
  const lineNumber = lineName.replace(/[^\d]/g, '')
  return lineNumber || '?'
}

// 计算距离百分比（用于进度条）
const getDistancePercentage = (distance: number): number => {
  const maxDistance = Math.max(...topDistances.value.map(item => item.distanceRaw))
  return (distance / maxDistance) * 100
}

// 处理站点点击事件 - 跳转到线路图中的站点
const handleStationClick = (stationName: string) => {
  if (jumpToStation) {
    jumpToStation(stationName)
  }
}

// 处理线路段点击事件 - 高亮线路段
const handleSegmentClick = (item: DistanceItem) => {
  if (highlightLineSegment) {
    
    // 清除之前的重置定时器
    if (highlightResetTimer.value) {
      clearTimeout(highlightResetTimer.value)
      highlightResetTimer.value = null
    }
    
    // 设置高亮状态（新的高亮会自动清除之前的高亮）
    isHighlighting.value = true
    
    highlightLineSegment(item.from, item.to, item.line)
    
    // 11秒后重置防抖状态（3秒动画 + 8秒高亮持续时间）
    highlightResetTimer.value = setTimeout(() => {
      isHighlighting.value = false
      highlightResetTimer.value = null
    }, 11000)
  } else {
    // 如果没有高亮功能，至少跳转到起点站
    if (jumpToStation) {
      jumpToStation(item.from)
    }
  }
}

// 处理线路点击事件 - 高亮整条线路
const handleLineClick = (lineName: string) => {
  // 由于还没有专门的高亮整条线路功能，暂时跳转到该线路的第一个站点
  const lineStations = stations.value.filter(station => station.rname === lineName)
  if (lineStations.length > 0 && jumpToStation) {
    // 找到该线路order最小的站点
    const firstStation = lineStations.sort((a, b) => a.order - b.order)[0]
    jumpToStation(firstStation.sname)
  } else {
  }
}

// 处理CSV数据
const parseCSV = (csvText: string): StationData[] => {
  const lines = csvText.trim().split('\n')
  
  return lines.slice(1).map(line => {
    // 更健壮的CSV解析，处理带引号的字段
    const fields: string[] = []
    let current = ''
    let inQuotes = false
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        fields.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    fields.push(current.trim()) // 添加最后一个字段
    
    return {
      sname: fields[0] || '',
      rname: fields[1] || '',
      order: parseInt(fields[2]) || 0,
      lat: parseFloat(fields[3]) || 0,
      lng: parseFloat(fields[4]) || 0,
      fork: parseInt(fields[5]) || 0
    }
  }).filter(station => station.sname && station.rname && station.lat && station.lng)
}

// 计算所有相邻站点间距离
const calculateAllDistances = (): DistanceItem[] => {
  const distances: DistanceItem[] = []
  
  if (!stations.value || stations.value.length === 0) {
    return distances
  }
  
  // 按线路分组
  const lineGroups = stations.value.reduce((groups, station) => {
    if (!station || !station.rname) return groups
    
    if (!groups[station.rname]) {
      groups[station.rname] = []
    }
    groups[station.rname].push(station)
    return groups
  }, {} as Record<string, StationData[]>)
  
  
  // 筛选1-18号线
  const filteredLineGroups = Object.entries(lineGroups).filter(([lineName]) => {
    const lineNumber = lineName.replace(/[^\d]/g, '')
    const num = parseInt(lineNumber)
    return num >= 1 && num <= 18
  })
  
  
  // 计算每条线路内相邻站点距离
  filteredLineGroups.forEach(([lineName, lineStations]) => {
    if (!lineStations || lineStations.length < 2) {
      return
    }
    
    // 按order排序
    const sortedStations = lineStations.sort((a, b) => a.order - b.order)
    
    // 检查是否为环线（起点站和终点站是同一个站）
    const isRingLine = sortedStations.length > 2 && 
                      sortedStations[0].sname === sortedStations[sortedStations.length - 1].sname
    
    // 根据线路处理分叉情况
    let mainLineStations: StationData[] = []
    
    if (lineName === '5号线') {
      // 5号线：只保留fork=1的主线站点，排除奉贤新城支线
      mainLineStations = sortedStations.filter(station => station.fork === 1)
    } else if (lineName === '10号线') {
      // 10号线：只保留fork=1的主线站点，排除虹桥机场支线
      mainLineStations = sortedStations.filter(station => station.fork === 1)
    } else if (lineName === '11号线') {
      // 11号线：只保留fork=1的主线站点，排除嘉定北支线
      mainLineStations = sortedStations.filter(station => station.fork === 1)
    } else if (lineName === '12号线') {
      // 12号线：排除七莘路到洞泾的延伸路段（order 1-6），只保留主线（从七莘路开始）
      mainLineStations = sortedStations.filter(station => station.order >= 7)
    } else {
      // 其他线路保持原逻辑
      mainLineStations = sortedStations
    }
    
    
    // 计算相邻站点距离，环线需要排除重复的首尾站点
    const endIndex = isRingLine ? mainLineStations.length - 1 : mainLineStations.length
    
    for (let i = 0; i < endIndex - 1; i++) {
      const from = mainLineStations[i]
      const to = mainLineStations[i + 1]
      
      // 验证坐标数据
      if (!from || !to || 
          typeof from.lat !== 'number' || typeof from.lng !== 'number' ||
          typeof to.lat !== 'number' || typeof to.lng !== 'number' ||
          isNaN(from.lat) || isNaN(from.lng) || isNaN(to.lat) || isNaN(to.lng)) {
        continue
      }
      
      const distance = calculateDistance(from.lat, from.lng, to.lat, to.lng)
      
      if (isNaN(distance) || distance <= 0) {
        continue
      }
      
      distances.push({
        from: from.sname || '未知站点',
        to: to.sname || '未知站点',
        line: lineName || '未知线路',
        distance: distance.toFixed(2),
        distanceRaw: distance,
        lineColor: getLineColor(lineName),
        fromCoords: { lat: from.lat, lng: from.lng },
        toCoords: { lat: to.lat, lng: to.lng }
      })
    }
  })
  
  return distances
}

// 加载数据
const loadData = async () => {
  try {
    loading.value = true
    
    // 同时加载站点数据和颜色数据
    const [stationsResponse, colorsResponse] = await Promise.all([
      fetch('/metro_station_location.csv'),
      fetch('/shanghai-metro-colors.json')
    ])
    
    if (!stationsResponse.ok || !colorsResponse.ok) {
      throw new Error('数据加载失败')
    }
    
    const [stationsText, colorsData] = await Promise.all([
      stationsResponse.text(),
      colorsResponse.json()
    ])
    
    stations.value = parseCSV(stationsText)
    lineColors.value = colorsData
    
    
    if (stations.value.length === 0) {
      throw new Error('没有有效的站点数据')
    }
    
    // 计算距离
    const allDistances = calculateAllDistances()
    
    
    if (allDistances.length === 0) {
      throw new Error('无法计算站点间距离')
    }
    
    // 获取前10长的距离
    topDistances.value = allDistances
      .sort((a, b) => b.distanceRaw - a.distanceRaw)
      .slice(0, 10)
    
    
  } catch (err) {
    error.value = `数据加载失败: ${err}`
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadData()
})

onUnmounted(() => {
  // 清理定时器，防止内存泄漏
  if (highlightResetTimer.value) {
    clearTimeout(highlightResetTimer.value)
    highlightResetTimer.value = null
  }
})
</script>

<style scoped>
.metro-distance-chart {
  position: relative;
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

/* 加载和错误状态 */
.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
  color: #64748b;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 紧凑型表格样式 */
.compact-distance-table {
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.table-header {
  display: grid;
  grid-template-columns: 50px 1fr 300px 80px;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.header-rank,
.header-stations,
.header-distance,
.header-line {
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-stations {
  justify-content: flex-start;
}

/* 距离行样式 */
.distance-row {
  display: grid;
  grid-template-columns: 50px 1fr 300px 80px;
  gap: 12px;
  padding: 12px 20px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInUp 0.4s ease forwards;
  cursor: pointer;
  position: relative;
}

.distance-row::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
  transition: width 0.3s ease;
  z-index: 0;
}

.distance-row:hover::before {
  width: 100%;
}

.distance-row:hover {
  background: transparent;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  border-radius: 8px;
}

.distance-row.highlighting {
  background: rgba(251, 191, 36, 0.1);
  cursor: pointer;
  opacity: 0.8;
  border-radius: 8px;
}

.distance-row.highlighting:hover {
  background: rgba(251, 191, 36, 0.2);
  transform: translateY(-1px);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
}

.distance-row > * {
  position: relative;
  z-index: 1;
}

.distance-row.top-rank {
  background: linear-gradient(90deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%);
  border-left: 3px solid #f59e0b;
}

/* 为非前三名的距离行添加左边装饰条 */
.distance-row:not(.top-rank) {
  border-left: 3px solid #3b82f6;
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 排名单元格 */
.rank-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.rank-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.rank-number.rank-1 { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.rank-number.rank-2 { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); }
.rank-number.rank-3 { background: linear-gradient(135deg, #cd7c0a 0%, #92400e 100%); }
.rank-number:not(.rank-1):not(.rank-2):not(.rank-3) { 
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); 
}

/* 站点单元格 */
.stations-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.station-connection {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.station-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.station-name.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.station-name.clickable:hover {
  color: #3b82f6;
  text-decoration: underline;
  transform: scale(1.02);
}

.connection-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  flex-shrink: 0;
}

/* 距离单元格 */
.distance-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.distance-info {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.distance-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.distance-unit {
  font-size: 11px;
  color: #6b7280;
}

.distance-bar-mini {
  width: 300px;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.distance-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease-out;
}

/* 线路单元格 */
.line-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.line-badge-compact {
  min-width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  padding: 0 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.line-badge-compact.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.line-badge-compact:hover {
  transform: scale(1.1);
}

.line-badge-compact.clickable:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

/* 图表说明 */
.chart-note {
  margin-bottom: 40px;
  padding: 12px 16px;
  background: rgba(59, 130, 246, 0.05);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
}

.chart-note p {
  margin: 0 0 8px 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}

.chart-note p:last-child {
  margin-bottom: 0;
}

.interaction-hint {
  color: #1e40af !important;
  font-size: 12px !important;
  background: rgba(59, 130, 246, 0.1);
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 8px !important;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .compact-distance-table {
    margin: 0 -10px 20px -10px;
    border-radius: 0;
  }
  
  .table-header,
  .distance-row {
    grid-template-columns: 40px 1fr 110px 60px;
    gap: 8px;
    padding: 12px 16px;
  }
  
  .table-header {
    font-size: 12px;
  }
  
  .rank-number {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }
  
  .station-name {
    font-size: 13px;
    max-width: 80px;
  }
  
  .distance-value {
    font-size: 14px;
  }
  
  .distance-unit {
    font-size: 10px;
  }
  
  .distance-bar-mini {
    width: 80px;
    height: 3px;
  }
  
  .line-badge-compact {
    min-width: 28px;
    height: 28px;
    font-size: 12px;
    padding: 0 6px;
  }
  
  .chart-note {
    padding: 10px 12px;
  }
  
  .chart-note p {
    font-size: 12px;
  }
}
</style>